// ============================================================
// useSpeechSynthesis — browser TTS for the Square Code Command
// Bar voice loop.
//
// First-pass TTS implementation per docs/COMMAND_VOICE_LOOP.md:
// uses the W3C Web Speech API (window.speechSynthesis) only. No
// paid TTS provider, no server-side audio synthesis. The browser
// is the sole speaker.
//
// Behavior:
//   • `available` — true iff window.speechSynthesis exists
//   • `enabled`   — operator toggle, persisted to localStorage
//                   ('square.command_bar.voice_out')
//   • `speak(text)` — utters the text only when both available and
//                     enabled. Otherwise it is a graceful no-op.
//                     Long results are clipped to a sensible cap.
//
// Voice output is always optional and never blocks command
// execution. If TTS is unavailable, commands still dispatch and
// terminate normally.
// ============================================================

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'square.command_bar.voice_out'
const MAX_UTTERANCE_LEN = 240

function readEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writeEnabled(value: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
  } catch {
    /* ignore */
  }
}

function detectAvailability(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.speechSynthesis !== 'undefined' &&
    typeof window.SpeechSynthesisUtterance !== 'undefined'
  )
}

export function useSpeechSynthesis() {
  const [available, setAvailable] = useState<boolean>(false)
  const [enabled, setEnabledState] = useState<boolean>(false)

  // Detection + initial enabled load — runs once after mount so
  // SSR / first render never reaches into window.
  useEffect(() => {
    setAvailable(detectAvailability())
    setEnabledState(readEnabled())
  }, [])

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next)
    writeEnabled(next)
    if (!next && typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel()
      } catch {
        /* ignore */
      }
    }
  }, [])

  const toggle = useCallback(() => setEnabled(!enabled), [enabled, setEnabled])

  const speak = useCallback(
    (text: string) => {
      if (!available || !enabled) return
      const cleaned = (text || '').trim()
      if (!cleaned) return
      try {
        const utterance = new window.SpeechSynthesisUtterance(
          cleaned.length > MAX_UTTERANCE_LEN
            ? cleaned.slice(0, MAX_UTTERANCE_LEN - 1) + '…'
            : cleaned,
        )
        utterance.rate = 1
        utterance.pitch = 1
        window.speechSynthesis.cancel()
        window.speechSynthesis.speak(utterance)
      } catch {
        /* TTS is best-effort; silent failure is honest here */
      }
    },
    [available, enabled],
  )

  const cancel = useCallback(() => {
    if (!available) return
    try {
      window.speechSynthesis.cancel()
    } catch {
      /* ignore */
    }
  }, [available])

  return { available, enabled, setEnabled, toggle, speak, cancel }
}
